import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "../../components/layout/Navbar";
import Footer from "../../components/layout/Footer";
import { useAuth } from "../../context/AuthContext";
import { api, errorMessage } from "../../lib/api";
import { Lightning, Trophy, Users, Wallet as WalletIcon } from "@phosphor-icons/react";
import { toast } from "sonner";

const TILES = [
    { to: "/respondent/surveys", label: "Available surveys", icon: Lightning, accent: "respondent" },
    { to: "/respondent/wallet", label: "Wallet & cashout", icon: WalletIcon, accent: "ink" },
    { to: "/respondent/profile", label: "Profile", icon: Users, accent: "ink" },
    { to: "/respondent/referrals", label: "Referrals", icon: Trophy, accent: "ink" },
];

export default function RespondentDashboard() {
    const { user } = useAuth();
    const [available, setAvailable] = useState([]);
    const [inProgress, setInProgress] = useState([]);
    const [cashouts, setCashouts] = useState([]);

    useEffect(() => {
        api.get("/surveys/available").then((r) => setAvailable(r.data || [])).catch(() => {});
        api.get("/responses/me/in-progress").then((r) => setInProgress(r.data || [])).catch(() => {});
        api.get("/cashout/me").then((r) => setCashouts(r.data || [])).catch(() => {});
    }, []);

    const pending = user?.status !== "approved";

    return (
        <div className="bg-paper min-h-screen">
            <Navbar />
            <main className="max-w-7xl mx-auto px-6 lg:px-8 py-12">
                <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
                    <div>
                        <p className="eyebrow mb-2">MEMBER · {user?.level?.toUpperCase()}</p>
                        <h1 className="h2" data-testid="resp-welcome">Hello, {user?.name?.split(" ")[0]}.</h1>
                        <p className="text-ink-600 mt-2 text-sm">
                            Streak: <span className="font-mono">{user?.streak_days || 0}d</span> ·{" "}
                            Lifetime: <span className="font-mono">{user?.lifetime_points || 0} pts</span>
                        </p>
                    </div>
                    <div className="card-base p-6 min-w-[280px]" data-testid="resp-balance-card">
                        <div className="eyebrow mb-1">CURRENT BALANCE</div>
                        <div className="font-mono text-4xl font-semibold tracking-tight">
                            {(user?.points || 0).toLocaleString()} <span className="text-respondent">pts</span>
                        </div>
                        <div className="text-sm text-ink-600 mt-1">
                            ≈ ${((user?.points || 0) / 100).toFixed(2)} cash equivalent
                        </div>
                    </div>
                </div>

                {pending && (
                    <div className="bg-warning/10 border border-warning/40 px-5 py-4 mb-8 text-sm" data-testid="resp-pending-banner">
                        <span className="font-semibold">Account pending approval.</span> Our admin team is reviewing your application — usually within a few hours. You can complete your profile in the meantime.
                    </div>
                )}

                {user && !user.email_verified && (
                    <div className="bg-info/10 border border-info/40 px-5 py-4 mb-8 text-sm flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3" data-testid="email-verify-banner">
                        <div>
                            <span className="font-semibold">Verify your email.</span> Click the link we sent to <span className="font-mono">{user.email}</span>. Don&apos;t see it?
                        </div>
                        <button
                            className="btn btn-ink text-xs py-2 px-3"
                            data-testid="resend-verification-btn"
                            onClick={async () => {
                                try {
                                    await api.post("/auth/send-verification");
                                    toast.success("Verification email sent");
                                } catch (e) {
                                    toast.error(errorMessage(e));
                                }
                            }}
                        >
                            Resend verification
                        </button>
                    </div>
                )}

                {/* Quick tiles */}
                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-0 border border-ink-200 mb-12">
                    {TILES.map((t, i) => {
                        const Icon = t.icon;
                        return (
                            <Link
                                key={t.to}
                                to={t.to}
                                className={`p-6 ${i < 3 ? "border-r border-ink-200" : ""} ${i < 2 ? "border-b sm:border-b-0 lg:border-b-0" : ""} hover:bg-cream`}
                                data-testid={`tile-${t.label.replace(/\s+/g, "-").toLowerCase()}`}
                            >
                                <Icon size={24} className={t.accent === "respondent" ? "text-respondent" : "text-ink"} />
                                <div className="font-heading font-bold text-lg mt-4">{t.label}</div>
                                <div className="font-mono text-[10px] text-ink-600 mt-1">→ OPEN</div>
                            </Link>
                        );
                    })}
                </div>

                {/* In-progress */}
                {inProgress.length > 0 && (
                    <section className="mb-12">
                        <p className="eyebrow mb-3">IN PROGRESS</p>
                        <div className="grid md:grid-cols-2 gap-4">
                            {inProgress.map((r) => (
                                <Link key={r.id} to={`/respondent/surveys/${r.survey_id}`} className="card-base p-5 hover:bg-cream" data-testid={`inprogress-${r.id}`}>
                                    <div className="font-heading font-bold">{r.survey?.title || "Survey"}</div>
                                    <div className="text-xs text-ink-600 mt-1">~{r.survey?.estimated_minutes} min · {r.survey?.points_per_response} pts</div>
                                    <div className="font-mono text-[10px] mt-2 text-respondent">RESUME →</div>
                                </Link>
                            ))}
                        </div>
                    </section>
                )}

                {/* Available surveys */}
                <section className="mb-12">
                    <div className="flex items-end justify-between mb-3">
                        <p className="eyebrow">AVAILABLE FOR YOU · {available.length}</p>
                        <Link to="/respondent/surveys" className="text-xs underline">See all</Link>
                    </div>
                    {pending ? (
                        <div className="card-base p-8 text-center text-ink-600 text-sm">
                            Surveys unlock once your account is approved.
                        </div>
                    ) : available.length === 0 ? (
                        <div className="card-base p-8 text-center text-ink-600 text-sm">
                            No surveys match your profile right now. Complete your{" "}
                            <Link to="/respondent/profile" className="underline">demographics</Link> to qualify for more.
                        </div>
                    ) : (
                        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                            {available.slice(0, 6).map((s) => (
                                <SurveyCard key={s.id} s={s} />
                            ))}
                        </div>
                    )}
                </section>

                {/* Recent cashouts */}
                {cashouts.length > 0 && (
                    <section>
                        <p className="eyebrow mb-3">RECENT CASHOUTS</p>
                        <div className="card-base">
                            {cashouts.slice(0, 5).map((c) => (
                                <div key={c.id} className="flex items-center justify-between p-4 border-b border-ink-200 last:border-b-0 text-sm">
                                    <div>
                                        <div className="font-mono">${c.usd.toFixed(2)} · {c.method}</div>
                                        <div className="text-xs text-ink-600">{new Date(c.created_at).toLocaleString()}</div>
                                    </div>
                                    <Tag status={c.status} />
                                </div>
                            ))}
                        </div>
                    </section>
                )}
            </main>
            <Footer />
        </div>
    );
}

export function SurveyCard({ s }) {
    return (
        <Link to={`/respondent/surveys/${s.id}`} className="card-base p-5 hover:bg-cream block" data-testid={`survey-card-${s.id}`}>
            <div className="flex items-center justify-between">
                <span className="font-mono text-[10px] text-ink-600">{s.category?.toUpperCase()}</span>
                <span className="font-mono text-xs text-respondent">+{s.points_per_response} pts</span>
            </div>
            <div className="font-heading font-bold text-lg mt-3 leading-snug">{s.title}</div>
            <div className="text-xs text-ink-600 mt-2 line-clamp-2">{s.description}</div>
            <div className="border-t border-ink-200 mt-4 pt-3 text-xs text-ink-600 flex justify-between">
                <span>~{s.estimated_minutes} min · {s.num_questions} q</span>
                <span>${(s.points_per_response / 100).toFixed(2)}</span>
            </div>
        </Link>
    );
}

function Tag({ status }) {
    const map = {
        pending: "bg-warning/15 text-warning",
        paid: "bg-success/15 text-success",
        rejected: "bg-error/15 text-error",
    };
    return <span className={`font-mono text-[10px] px-2 py-1 ${map[status] || "bg-ink-100"}`}>{status.toUpperCase()}</span>;
}
